import { useEffect, useState } from "react";
import Img from "../img";
import { useUser, useNotifications } from "../../contexts";
import shared from "../../data/js/shared";
import SideRail, { type RailItem } from "../side-rail";

/**
 * Left-hand navigation rail for the account area. Rendered once by AllLayout for
 * any /account route, replacing the old tic-tac-toe tile grid.
 *
 * The markup, styling and mobile drawer live in components/side-rail.tsx, which
 * the dash uses too; this file supplies the account-specific parts — which items
 * to show, the unread badges, and the "Post Listing" call to action.
 *
 * Every item here is shown to every signed-in user. Discounts and Notifications
 * were once admin-only while the settings underneath them were incomplete; both
 * were opened to everyone in September 2026 and nothing in this file filters any
 * more. The one thing still gated is a single row INSIDE the notifications page
 * — the admin site digest — because only admins are on its recipient list.
 */
export default function AccountRail({ pathname }: { pathname: string }) {
  const { profile } = useUser();
  const { unreadSalesCount, unreadPurchasesCount, unreadMessagesCount } =
    useNotifications();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- client-mount guard so the wallet badge only renders after hydration (its value differs between server and first client render)
    setMounted(true);
  }, []);

  const items = shared.account.navButtons as RailItem[];

  const badgeFor = (href: string): number => {
    if (href.includes("sales")) return unreadSalesCount;
    if (href.includes("purchases")) return unreadPurchasesCount;
    if (href.includes("chats")) return unreadMessagesCount;
    if (href.includes("wallet") && mounted && profile && profile.trustLevel < 2)
      return 1;
    return 0;
  };

  return (
    <SideRail
      pathname={pathname}
      items={items}
      basePath="/account"
      name="Account"
      testIdPrefix="account-rail"
      renderIcon={(icon) => <Img src={icon} />}
      badgeFor={badgeFor}
      cta={{ label: "Post Listing", href: "/sell", icon: "/images/svg/camera.svg" }}
    />
  );
}
